import { useMemo, useState } from 'react';
import {
  Anchor, Stack, Title, Group, Button, TextInput, Table, Badge, Text,
  ActionIcon, Tooltip, Skeleton, Checkbox, Alert,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconSearch, IconTrash, IconAlertCircle } from '@tabler/icons-react';
import { DateTime } from 'luxon';
import fuzzysort from 'fuzzysort';
import { useUploadProfiles } from '../../hooks/useUploadProfiles';
import { useDeleteUploadProfiles } from '../../hooks/useUploadProfileMutations';
import { UploadProfileDrawer } from './UploadProfileDrawer';
import type { IngestcfgData } from '@/lib/types/Ingest';

export function UploadProfilesPage() {
  const { uploadProfiles, isLoading, isError } = useUploadProfiles();
  const deleteMutation = useDeleteUploadProfiles();

  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [drawerProfile, setDrawerProfile] = useState<IngestcfgData | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filteredProfiles = useMemo(() => {
    if (!search.trim()) {
      return [...uploadProfiles].sort((a, b) => a.label.localeCompare(b.label));
    }
    const results = fuzzysort.go(search, uploadProfiles, {
      keys: ['label'],
      threshold: -1000,
    });
    return results.map(r => r.obj);
  }, [uploadProfiles, search]);

  const allSelected = filteredProfiles.length > 0 && filteredProfiles.every(p => selectedIds.has(p.ingestcfgId));
  const someSelected = filteredProfiles.some(p => selectedIds.has(p.ingestcfgId));

  function toggleAll() {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredProfiles.map(p => p.ingestcfgId)));
    }
  }

  function toggleOne(id: number) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleCreate() {
    setDrawerProfile(null);
    setDrawerOpen(true);
  }

  function handleEdit(profile: IngestcfgData) {
    setDrawerProfile(profile);
    setDrawerOpen(true);
  }

  function handleDrawerClose() {
    setDrawerOpen(false);
    setDrawerProfile(null);
  }

  function handleDrawerSuccess() {
    setDrawerOpen(false);
    setDrawerProfile(null);
  }

  function handleDeleteSingle(profile: IngestcfgData) {
    modals.openConfirmModal({
      title: 'Delete Upload Profile',
      children: (
        <Text size="sm">
          Are you sure you want to delete <Text span fw={600}>{profile.label}</Text>?
          This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync([profile.ingestcfgId]);
          setSelectedIds(prev => {
            const next = new Set(prev);
            next.delete(profile.ingestcfgId);
            return next;
          });
          notifications.show({ title: 'Deleted', message: `Upload profile "${profile.label}" removed.`, color: 'green' });
        } catch (err) {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to delete upload profile.',
            color: 'red',
          });
        }
      },
    });
  }

  function handleBatchDelete() {
    const count = selectedIds.size;
    if (count === 0) return;

    const label = count === 1 ? '1 Upload Profile' : `${count} Upload Profiles`;
    modals.openConfirmModal({
      title: `Delete ${label}`,
      children: (
        <Text size="sm">
          Are you sure you want to delete {label.toLowerCase()}?
          This action cannot be undone.
        </Text>
      ),
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await deleteMutation.mutateAsync([...selectedIds]);
          setSelectedIds(new Set());
          notifications.show({ title: 'Deleted', message: `${label} removed.`, color: 'green' });
        } catch (err) {
          notifications.show({
            title: 'Error',
            message: err instanceof Error ? err.message : 'Failed to delete upload profiles.',
            color: 'red',
          });
        }
      },
    });
  }

  if (isLoading) {
    return (
      <Stack gap="lg">
        <Title order={2}>Upload Profiles</Title>
        <Skeleton height={40} radius="md" />
        <Skeleton height={400} radius="md" />
      </Stack>
    );
  }

  if (isError) {
    return (
      <Stack gap="lg">
        <Title order={2}>Upload Profiles</Title>
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          Failed to load upload profiles. Please try again later.
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Upload Profiles</Title>
        <Group gap="sm">
          {selectedIds.size > 0 && (
            <Button
              variant="light"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleBatchDelete}
              loading={deleteMutation.isPending}
            >
              Delete ({selectedIds.size})
            </Button>
          )}
          <Button leftSection={<IconPlus size={16} />} onClick={handleCreate}>
            Create Upload Profile
          </Button>
        </Group>
      </Group>

      <TextInput
        placeholder="Search by name..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        aria-label="Search upload profiles"
      />

      {filteredProfiles.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          {search ? 'No upload profiles match your search.' : 'No upload profiles yet.'}
        </Text>
      ) : (
        <Table highlightOnHover aria-label="Upload profiles">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>Tags</Table.Th>
              <Table.Th>Created</Table.Th>
              <Table.Th>Updated</Table.Th>
              <Table.Th ta="right">Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredProfiles.map(profile => (
              <UploadProfileRow
                key={profile.ingestcfgId}
                profile={profile}
                selected={selectedIds.has(profile.ingestcfgId)}
                onToggle={() => toggleOne(profile.ingestcfgId)}
                onEdit={() => handleEdit(profile)}
                onDelete={() => handleDeleteSingle(profile)}
              />
            ))}
          </Table.Tbody>
        </Table>
      )}

      <UploadProfileDrawer
        opened={drawerOpen}
        profile={drawerProfile}
        onClose={handleDrawerClose}
        onSuccess={handleDrawerSuccess}
      />
    </Stack>
  );
}

// ── Row component ──────────────────────────────────────────────────

const MAX_VISIBLE_TAGS = 3;

function UploadProfileRow({
  profile,
  selected,
  onToggle,
  onEdit,
  onDelete,
}: {
  profile: IngestcfgData;
  selected: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <Table.Tr>
      <Table.Td>
        <Checkbox checked={selected} onChange={onToggle} aria-label={`Select ${profile.label}`} />
      </Table.Td>
      <Table.Td>
        <Anchor size="sm" fw={500} onClick={onEdit} underline="hover" c="inherit">
          {profile.label}
        </Anchor>
      </Table.Td>
      <Table.Td>
        <TagsCell tags={profile.tags} />
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {DateTime.fromSeconds(profile.created).toLocaleString(DateTime.DATE_MED)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">
          {DateTime.fromSeconds(profile.updated).toLocaleString(DateTime.DATE_MED)}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs" justify="flex-end">
          <Tooltip label="Delete">
            <ActionIcon variant="subtle" color="red" onClick={onDelete} aria-label={`Delete ${profile.label}`}>
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

// ── Tags cell with overflow ──────────────────────────────────────

function TagsCell({ tags }: { tags: string[] }) {
  if (tags.length === 0) {
    return <Text size="xs" c="dimmed">None</Text>;
  }

  const visible = tags.slice(0, MAX_VISIBLE_TAGS);
  const overflow = tags.length - MAX_VISIBLE_TAGS;

  return (
    <Group gap={4}>
      {visible.map(tag => (
        <Badge key={tag} variant="light" size="xs">
          {tag}
        </Badge>
      ))}
      {overflow > 0 && (
        <Tooltip label={tags.slice(MAX_VISIBLE_TAGS).join(', ')}>
          <Badge variant="light" color="gray" size="xs">
            +{overflow} more
          </Badge>
        </Tooltip>
      )}
    </Group>
  );
}
